import React, { useEffect, useState, useRef, useMemo, useContext } from "react";
import { Box, Typography, Grid, Select, MenuItem, IconButton, Skeleton, Stack, FormControl, Tabs, Tab, useMediaQuery } from "@mui/material";
import { Bar, Pie } from "react-chartjs-2";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

import DI from "../../utility/DI";
import { getCategoryLabel } from "./Home";
import { FetchContext } from "../SideBar";
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const HomeBoard = (props) => {
  const [loading, setLoading] = useState(true);
  const [barChartLoading, setBarChartLoading] = useState(false);
  const [pieChartLoading, setPieChartLoading] = useState(false);
  const [data, setData] = useState(null);
  const [timeRange, setTimeRange] = useState("monthly"); // Default time range for bar chart
  const [currentPage, setCurrentPage] = useState(1); // Pagination state for bar chart
  const [activeTab, setActiveTab] = useState(0); // 0 for Bar Chart, 1 for Pie Chart
  const [selectedMonth, setSelectedMonth] = useState("all"); // Selected month for pie chart
  const [pieChartData, setPieChartData] = useState(null); // Separate data for pie chart
  const [monthlyOptions, setMonthlyOptions] = useState([]); // Store monthly options separately for pie chart
  const itemsPerPage = 10; // Items per page

  const chartRef = useRef(null); // Reference to the chart container
  const previousCategoryData = useRef(null); // To store the previous category data for comparison
  const chartHeight = chartRef.current ? chartRef.current.offsetHeight : 300; // Default height if ref is not set

  const isMobile = useMediaQuery("(max-width: 768px)"); // Detect if the screen width is less than 768px

  const {
    currentHome,
    di: { GET, error, urls, session },
  } = props;

  const { refresh } = useContext(FetchContext);
  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch data for bar chart
      const barParams = {
        user_id: session?.id,
        home_id: currentHome,
        time_range: timeRange,
        page: currentPage,
        items_per_page: itemsPerPage,
      };

      const barRes = await GET(urls.get.get_dashboard_insights, barParams);
      if (barRes?.success) {
        setData(barRes);
      } else {
        error(barRes?.message ?? "Error fetching bar chart data");
      }

      // Only update pie chart data if selectedMonth is "all" (meaning we want all data)
      // If a specific month is selected, we don't want to overwrite the pie chart data
      if (selectedMonth === "all") {
        setPieChartData(barRes?.success ? barRes : null);
      }
    } catch {
      error("Oops..! session out. Refresh the page");
    } finally {
      setLoading(false);
    }
  };

  // Function to extract months from data
  const extractMonthsFromData = (data) => {
    if (!data?.aggregated_expenses) return [];

    const monthsSet = new Set();

    data.aggregated_expenses.forEach(expense => {
      // For monthly format like "December 2025", it's already in the right format
      if (expense.grouped_time) {
        monthsSet.add(expense.grouped_time);
      }
    });

    // Sort months in descending order (most recent first)
    return Array.from(monthsSet).sort((a, b) => {
      // Parse month-year strings to compare them properly
      // Convert "Month Year" format to Date object
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB - dateA; // Descending order
    });
  };

  // Get unique months from stored monthly options
  const getAvailableMonths = () => {
    return monthlyOptions;
  };

  // Effect for bar chart data only
  useEffect(() => {
    const fetchBarData = async () => {
      try {
        setBarChartLoading(true);

        // Fetch data for bar chart
        const barParams = {
          user_id: session?.id,
          home_id: currentHome,
          time_range: timeRange,
          page: currentPage,
          items_per_page: itemsPerPage,
        };

        const barRes = await GET(urls.get.get_dashboard_insights, barParams);
        if (barRes?.success) {
          setData(barRes);
        } else {
          error(barRes?.message ?? "Error fetching bar chart data");
        }
      } catch {
        error("Oops..! session out. Refresh the page");
      } finally {
        setBarChartLoading(false);
      }
    };

    fetchBarData();
  }, [timeRange, currentPage, currentHome, session?.id, GET, urls.get.get_dashboard_insights, error]);

  // Effect to update pie chart data when main data changes (e.g., when switching homes)
  useEffect(() => {
    if (data) {
      // Update pie chart data with the same data as bar chart when main data changes
      // But only if no specific month is selected for the pie chart
      if (selectedMonth === "all") {
        setPieChartData(data);
      }
    } else {
      // If no data for the current home, reset pie chart data
      setPieChartData(null);
    }
  }, [data, selectedMonth]);

  // Effect for pie chart data only
  useEffect(() => {
    const fetchPieData = async () => {
      try {
        setPieChartLoading(true);
        if (selectedMonth !== "all") {
          // Fetch specific month data for pie chart
          const pieParams = {
            user_id: session?.id,
            home_id: currentHome,
            time_range: "monthly", // Always use monthly for pie chart when filtering by month
            page: 1, // Page doesn't matter for pie chart when filtering by month
            items_per_page: 100, // Get all data for pie chart
            selected_month: selectedMonth,
          };

          const pieRes = await GET(urls.get.get_dashboard_insights, pieParams);
          if (pieRes?.success) {
            setPieChartData(pieRes);
          } else {
            error(pieRes?.message ?? "Error fetching pie chart data");
          }
        } else {
          // If 'all' is selected, use the main data for pie chart
          // But only if main data is already loaded
          if (data) {
            setPieChartData(data);
          }
        }
      } catch {
        error("Oops..! session out. Refresh the page");
      } finally {
        setPieChartLoading(false);
      }
    };

    fetchPieData();
  }, [selectedMonth, currentHome, session?.id, GET, urls.get.get_dashboard_insights, error, data]);

  // Effect for initial load and refresh - only for getting monthly options
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Fetch monthly data separately to populate the month selector options
        const monthlyParams = {
          user_id: session?.id,
          home_id: currentHome,
          time_range: "monthly",
          page: 1,
          items_per_page: 100, // Get all months
        };

        const monthlyRes = await GET(urls.get.get_dashboard_insights, monthlyParams);
        if (monthlyRes?.success) {
          setMonthlyOptions(extractMonthsFromData(monthlyRes));
        } else {
          error(monthlyRes?.message ?? "Error fetching monthly data for options");
        }
      } catch {
        error("Oops..! session out. Refresh the page");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [refresh, currentHome, session?.id, GET, urls.get.get_dashboard_insights, error]);

  // Memoize the Pie Chart data to prevent unnecessary re-renders
  const categoryPieData = useMemo(() => {
    // Use pieChartData if available, otherwise fall back to main data
    const pieDataSrc = pieChartData || data;

    if (
      previousCategoryData.current &&
      JSON.stringify(previousCategoryData.current) === JSON.stringify(pieDataSrc?.category_expenses)
    ) {
      return previousCategoryData.current; // Return the previous data if it's the same
    }

    const pieData = {
      labels: pieDataSrc?.category_expenses.map((item) => getCategoryLabel(item.category)) || [],
      datasets: [
        {
          label: "Category Expenses",
          data: pieDataSrc?.category_expenses.map((item) => item.total_expense) || [],
          backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF"],
        },
      ],
    };

    previousCategoryData.current = pieData; // Update the reference with the new data
    return pieData;
  }, [pieChartData?.category_expenses, data?.category_expenses]);

  // Bar Chart data for aggregated expenses
  const aggregatedExpensesData = {
    labels: data?.aggregated_expenses.map((item) => item.grouped_time) || [],
    datasets: [
      {
        label: `Expenses (${timeRange})`,
        data: data?.aggregated_expenses.map((item) => item.total_expense) || [],
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };
  const paginations=activeTab === 0 ?  <Grid item xs={12}>
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      {/* Time Range Selector */}
      <Box>
        <FormControl sx={{ m: 0, minWidth: 120 }} size="small">
          <Select
            value={timeRange}
            sx={{ minWidth: "200px" }}
            onChange={(e) => {
              setTimeRange(e.target.value);
              setCurrentPage(1); // Reset to first page
            }}
            fullWidth
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Pagination Controls */}
      <Box>
        <IconButton
          color="primary"
          disabled={currentPage === 1 || loading}
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        >
          <ArrowBack />
        </IconButton>
        <IconButton
          color="primary"
          disabled={data?.aggregated_expenses.length < itemsPerPage || loading}
          onClick={() => setCurrentPage((prev) => prev + 1)}
        >
          <ArrowForward />
        </IconButton>
      </Box>
    </Stack>
  </Grid>:null;

  // Month selector for pie chart - show on both mobile and desktop when pie chart would be active
  const monthSelector = (
    <Grid item xs={12}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
        <Box>
          <FormControl sx={{ m: 0, minWidth: 150 }} size="small">
            <Select
              value={selectedMonth}
              sx={{ minWidth: "200px" }}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
              }}
              fullWidth
              disabled={isMobile && activeTab !== 1} // Disable on mobile when not viewing pie chart
            >
              <MenuItem value="all">All Months</MenuItem>
              {getAvailableMonths().map((month, index) => (
                <MenuItem key={index} value={month}>{month}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>
    </Grid>
  );

  // Conditional rendering for mobile vs desktop
  const showMonthSelector = isMobile ? activeTab === 1 : true; // Show on desktop always, on mobile only when pie chart active

  return (
    <Box sx={{ padding: "5px" }}>
      <Grid container spacing={3}>
        {/* Overall Total Expense */}
        <Grid item xs={12}>
          <Typography variant="h6">Overall Total Expense</Typography>
          <Typography variant="h4">₹{Number(data?.overall_total_expense || 0).toFixed(2)}</Typography>
        </Grid>



        {/* Chart Display */}
        {isMobile ? (
          <>
            {/* Tabs for Bar and Pie Charts */}
            <Grid item xs={12} style={{ margin: 0, paddingTop: 0,marginBottom:'10px' }}>
              <Tabs
                sx={{ m: 0, paddingTop: 0 }}
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                indicatorColor="primary"
                textColor="primary"
                centered
              >
                <Tab label="Aggregated Exp." />
                <Tab label="Overall Categories" />
              </Tabs>
            </Grid>

            <Grid item xs={12} style={{ margin: 0, paddingTop: "5px" }}>
              {activeTab === 0 ? (
                <Box ref={chartRef}>
                  {barChartLoading ? (
                    <Skeleton
                      variant="rectangular"
                      animation="wave"
                      height={chartHeight}
                      sx={{ borderRadius: "4px" }}
                    />
                  ) : (
                    <>
                    {paginations}
                    <Bar data={aggregatedExpensesData} height={200} />
                    </>
                  )}
                </Box>
              ) : (
                <Box>
                  {pieChartLoading ? (
                    <>
                      {showMonthSelector && monthSelector}
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        height={chartHeight}
                        sx={{ borderRadius: "4px" }}
                      />
                    </>
                  ) : (
                    <>
                      {showMonthSelector && monthSelector}
                      <Pie data={categoryPieData} />
                    </>
                  )}
                </Box>
              )}
            </Grid>
          </>
        ) : (
          <>
            {/* Show both charts side by side on larger screens */}
            <Grid item xs={6} >
              <Box ref={chartRef}>
                {barChartLoading ? (
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    height={chartHeight}
                    sx={{ borderRadius: "4px" }}
                  />
                ) : (
                  <>
                  {paginations}
                  <Bar data={aggregatedExpensesData} height={200} />
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={6} >
              <Box>
                {/* Month selector for desktop - always show since both charts are visible */}
                {showMonthSelector && monthSelector}
                <Box style={{height:(chartHeight+50)+'px',paddingTop:'10px'}}>
                  {pieChartLoading ? (
                    <Skeleton
                      variant="rectangular"
                      animation="wave"
                      height={chartHeight}
                      sx={{ borderRadius: "4px" }}
                    />
                  ) : (
                    <Pie data={categoryPieData}/>
                  )}
                </Box>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default DI(HomeBoard);